import { useRef, type DragEvent } from 'react';

type DragProps = {
  draggable: boolean;
  onDragStart?: () => void;
  onDragOver?: (e: DragEvent) => void;
  onDrop?: () => void;
  onDragEnd?: () => void;
};

/**
 * Drag-to-reorder by source index. Returns a function that gives the drag props
 * for the item at an index. A drop on another index calls `onMove(from, to)`.
 * Nothing is draggable while `enabled` is false. A drag that ends elsewhere
 * clears the stored source, so a later dragOver or drop never misreads it.
 */
export function useIndexDragReorder(
  onMove: ((from: number, to: number) => void) | undefined,
  enabled = true
): (index: number) => DragProps {
  const source = useRef<number | null>(null);
  return index => {
    if (!enabled || !onMove) return { draggable: false };
    return {
      draggable: true,
      onDragStart: () => {
        source.current = index;
      },
      // Allow a drop only while one of our own drags is active.
      onDragOver: e => {
        if (source.current !== null) e.preventDefault();
      },
      onDrop: () => {
        const from = source.current;
        source.current = null;
        if (from !== null && from !== index) onMove(from, index);
      },
      onDragEnd: () => {
        source.current = null;
      },
    };
  };
}
